import React from "react";

import firebase from "firebase/compat/app";
import "firebase/compat/analytics";

import { Box, Typography, Breadcrumbs, Link, Tabs, Tab } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useRecoilValue } from "recoil";


import MessageData, {
  GroupType,
  updateMessageGroupUserActivity,
} from "../../../data/MessageData";
import AddGroupDialog from "./messages/AddGroupDialog";
import GroupTable from "./messages/GroupTable";
import useOrganizationUsers from "../../../hooks/useOrganizationUsers";
import organizationIdSelector from "../../../atoms/organizationIdSelector";
import useMessageGroupData from "../../../hooks/useMessageGroupData";
import ModernChatWrapper from "../../../components/organisms/ModernChatWrapper";
import AISupportChat from "../../../components/organisms/chat/AISupportChat";
import { ChatParticipantProvider } from "../../../contexts/ChatParticipantContext";
import useEvents from "../../../hooks/useEvents";
import { WebAnalytics, ANALYTICS_EVENTS } from "@mighty45/analytics-shared";
import PageHeader from "../../../components/PageHeader";
import PageContent from "../../../components/PageContent";
import NewColors from "../../../themes/NewColors";
import { Button } from "@mui/material";
import SmartToyIcon from "@mui/icons-material/SmartToy";

const MessagesPage = () => {
  const navigate = useNavigate();
  const events = useEvents();
  const analytics = React.useMemo(() => new WebAnalytics({}), []);

  const organizationId = useRecoilValue(organizationIdSelector);

  const [mode, setMode] = React.useState("list");
  const [groups, setGroups] = React.useState([]);
  const [groupsLoading, setGroupsLoading] = React.useState(true);
  const [groupGroups, setGroupGroups] = React.useState([]);
  const [groupGroupsLoading, setGroupGroupsLoading] = React.useState(true);
  const [alertGroups, setAlertGroups] = React.useState([]);
  const [alertGroupsLoading, setAlertGroupsLoading] = React.useState(true);
  const [editGroupOpen, setEditGroupOpen] = React.useState(false);
  const [editGroupMode, setEditGroupMode] = React.useState("add");
  const [tabIndex, setTabIndex] = React.useState(0);
  const [isAIBotChat, setIsAIBotChat] = React.useState(false);

  // Stable UID reference — read once and memoize so it never causes re-renders
  const uid = React.useMemo(() => localStorage.getItem("uid"), []);
  const usersArray = React.useMemo(() => [uid], [uid]);

  // Snapshot callbacks have no dependency on `mode`.
  // The mode guard was previously here, but it caused all three subscriptions
  // to tear down and recreate on every list↔detail navigation.
  // The guard is no longer needed: updates arriving while in detail mode are
  // harmless state updates to the list data, which is just not rendered.
  const onMessageGroupSnapshot = React.useCallback((snapshot) => {
    setGroups(snapshot);
    setGroupsLoading(false);
  }, []);

  const onGroupMessageGroupSnapshot = React.useCallback((snapshot) => {
    setGroupGroups(snapshot);
    setGroupGroupsLoading(false);
  }, []);

  const onAlertMessageGroupSnapshot = React.useCallback((snapshot) => {
    setAlertGroups(snapshot);
    setAlertGroupsLoading(false);
  }, []);

  // Track messages page view (once on mount)
  React.useEffect(() => {
    analytics.trackScreenView("Messages", {
      organization_id: organizationId,
    });

    analytics.trackCustomEvent(ANALYTICS_EVENTS.FEATURE_USED, {
      feature: "messaging",
      organization_id: organizationId,
    });
  }, []); // Only track on mount

  // Track tab/mode changes separately
  React.useEffect(() => {
    analytics.trackCustomEvent(ANALYTICS_EVENTS.TAB_SWITCHED, {
      screen: "Messages",
      tab: tabIndex,
      mode: mode,
      organization_id: organizationId,
    });
  }, [mode, tabIndex]); // Track only when tab/mode actually changes

  // Subscribe only to the active tab's group type.
  // All three subscriptions were previously open simultaneously regardless of
  // which tab was visible. Now each subscription activates when its tab index
  // is selected and tears down when another tab is selected.
  useMessageGroupData({
    users: usersArray,
    order: "lastDate",
    direction: "desc",
    groupType: tabIndex === 0 ? GroupType.single : null,
    onSnapshot: onMessageGroupSnapshot,
    disabled: tabIndex !== 0,
  });

  useMessageGroupData({
    users: usersArray,
    order: "lastDate",
    direction: "desc",
    groupType: tabIndex === 1 ? GroupType.group : null,
    onSnapshot: onGroupMessageGroupSnapshot,
    disabled: tabIndex !== 1,
  });

  useMessageGroupData({
    users: usersArray,
    order: "lastDate",
    direction: "desc",
    groupType: tabIndex === 2 ? GroupType.alert : null,
    onSnapshot: onAlertMessageGroupSnapshot,
    disabled: tabIndex !== 2,
  });

  const users = useOrganizationUsers({
    organizationId,
  });

  const selectedGroup = React.useRef();

  React.useEffect(() => {
    return () => {
      if (navigate.action === "POP") {
        navigate("/home");
      }
    };
  }, [navigate]);

  React.useEffect(() => {
    firebase.analytics().logEvent("page_view", {
      page_title: "Messages Page",
      page_location: "/messages",
    });
  }, []);

  const handleChange = (event, newValue) => {
    events.logTabSwitch({
      pageName: "Messages",
      tabName: newValue,
      lastTab: tabIndex,
    });

    // Track with our analytics
    const tabNames = ["Client Groups", "Custom Groups", "Alert Groups"];
    analytics.trackCustomEvent("user_action", {
      action: "message_tab_changed",
      element: "tab_button",
      from_tab: tabNames[tabIndex] || "unknown",
      to_tab: tabNames[newValue] || "unknown",
      organization_id: organizationId,
    });
    setTabIndex(newValue);
    localStorage.setItem("messagePage_TabIndex", newValue);
  };

  if (mode === "list") {
    return (
      <PageContent
        header={
          <PageHeader 
            title="Messages" 
            subtitle="Communicate with your clients and track conversation history"
          />
        }
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Tabs value={tabIndex} onChange={handleChange}>
            <Tab label="Client Groups"></Tab>
            <Tab label="Custom Groups"></Tab>
            <Tab label="Alert Groups"></Tab>
          </Tabs>

          <Button
            variant="contained"
            startIcon={<SmartToyIcon sx={{ fontSize: 20 }} />}
            onClick={() => {
              setIsAIBotChat(true);
              setMode("detail");
              selectedGroup.current = { id: "m45-bot-support", name: "M45 Bot" };
              analytics.trackCustomEvent("user_action", {
                action: "ai_bot_chat_opened",
                element: "ai_bot_button",
                organization_id: organizationId,
              });
            }}
            sx={{
              backgroundColor: NewColors.brand.primary,
              color: "white",
              borderRadius: 2,
              whiteSpace: "nowrap",
              textTransform: "none",
              fontWeight: 600,
              px: 2,
              py: 1,
              "&:hover": {
                backgroundColor: "#2563EB",
              },
            }}
          >
            Ask M45 Bot
          </Button>
        </Box>

        <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden", pt: 1.5 }}>
          <Box
            role="tabpanel"
            hidden={tabIndex !== 0}
            id="messages-tabpanel-0"
            aria-labelledby="messages-tab-0"
            sx={{ flex: 1, display: tabIndex === 0 ? "flex" : "none", flexDirection: "column", minHeight: 0 }}
          >
            <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
              <GroupTable
            id="clientGroups"
            title="Client Groups"
            users={users}
            groups={groups}
            loading={groupsLoading}
            onGroupClicked={(group) => {
              selectedGroup.current = group;

              events.logButtonClick({
                buttonName: "Select Message Group",
                pageName: "Messages",
                tabName: "Client Groups",
              });

              // Track with our analytics
              analytics.trackCustomEvent("user_action", {
                action: "message_group_selected",
                element: "group_row",
                group_id: group.id,
                group_name: group.name,
                group_type: "client_groups",
                organization_id: organizationId,
              });

              analytics.trackCustomEvent(ANALYTICS_EVENTS.MESSAGE_RECEIVED, {
                group_id: group.id,
                group_type: "client_groups",
                organization_id: organizationId,
              });

              updateMessageGroupUserActivity(
                localStorage.getItem("uid"),
                group.id,
              );

              setMode("detail");
            }}
            editClicked={(group) => {
              events.logButtonClick({
                buttonName: "Edit Message Group",
                pageName: "Messages",
                tabName: "Client Groups",
              });

              selectedGroup.current = group;
              setEditGroupMode("edit");
              setEditGroupOpen(true);
              // Show edit dialog
            }}
              />
            </Box>
          </Box>

          <Box
            role="tabpanel"
            hidden={tabIndex !== 1}
            id="messages-tabpanel-1"
            aria-labelledby="messages-tab-1"
            sx={{ flex: 1, display: tabIndex === 1 ? "flex" : "none", flexDirection: "column", minHeight: 0 }}
          >
            <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
              <GroupTable
            id="customGroups"
            title="Custom Groups"
            users={users}
            groups={groupGroups}
            loading={groupGroupsLoading}
            onAddGroupClicked={() => {
              events.logButtonClick({
                buttonName: "Add Message Group",
                pageName: "Messages",
                tabName: "Custom Groups",
              });

              // Track with our analytics
              analytics.trackCustomEvent("user_action", {
                action: "add_message_group_clicked",
                element: "add_group_button",
                group_type: "custom_groups",
                organization_id: organizationId,
              });

              selectedGroup.current = undefined;
              setEditGroupMode("add");
              setEditGroupOpen(true);
            }}
            onGroupClicked={(group) => {
              selectedGroup.current = group;
              events.logButtonClick({
                buttonName: "Select Message Group",
                pageName: "Messages",
                tabName: "Custom Groups",
              });

              updateMessageGroupUserActivity(
                localStorage.getItem("uid"),
                group.id,
              );

              setMode("detail");
            }}
            editClicked={(group) => {
              selectedGroup.current = group;
              setEditGroupMode("edit");
              setEditGroupOpen(true);
              // Show edit dialog
            }}
              />
            </Box>
          </Box>

          <Box
            role="tabpanel"
            hidden={tabIndex !== 2}
            id="messages-tabpanel-2"
            aria-labelledby="messages-tab-2"
            sx={{ flex: 1, display: tabIndex === 2 ? "flex" : "none", flexDirection: "column", minHeight: 0 }}
          >
            <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
              <GroupTable
            id="alertGroups"
            title="Alert Groups"
            users={users}
            groups={alertGroups}
            loading={alertGroupsLoading}
            onGroupClicked={(group) => {
              events.logButtonClick({
                buttonName: "Select Message Group",
                pageName: "Messages",
                tabName: "Alert Groups",
              });
              selectedGroup.current = group;
              setMode("detail");
            }}
            onAddGroupClicked={() => {
              events.logButtonClick({
                buttonName: "Add Message Group",
                pageName: "Messages",
                tabName: "Alert Group",
              });
              selectedGroup.current = undefined;
              setEditGroupMode("add");
              setEditGroupOpen(true);
            }}
            editClicked={(group) => {
              events.logButtonClick({
                buttonName: "Edit Message Group",
                pageName: "Messages",
                tabName: "Alert Group",
              });
              selectedGroup.current = group;
              setEditGroupMode("edit");
              setEditGroupOpen(true);
              // Show edit dialog
            }}
              />
            </Box>
          </Box>
        </Box>

        {editGroupOpen && (
          <AddGroupDialog
            open={editGroupOpen}
            mode={editGroupMode}
            group={selectedGroup.current}
            onDialogClosed={async (groupInput, mode, editedGroup) => {
              setEditGroupOpen(false);
              if (groupInput) {
                if (mode === "edit") {
                  await MessageData.editMessageGroup(
                    editedGroup.id,
                    groupInput,
                  );
                } else {
                  const data = {
                    ...groupInput,
                    lastMessage: "No messages yet",
                    lastDate: new Date(),
                    type: groupInput.type,
                  };
                  await MessageData.addMessageGroup(data);
                }
              }
            }}
          />
        )}

      </PageContent>
    );
  } else {
    return (
      <PageContent fullHeight disablePadding>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            overflow: "hidden",
          }}
          data-testid="messages-page"
        >
          <Box
            sx={{
              flexShrink: 0, // Don't shrink the breadcrumb area
              px: 2.5,
              py: 1.5,
              borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
              backgroundColor: NewColors.cardBackground,
            }}
            data-testid="breadcrumb"
          >
            <Breadcrumbs separator=">">
              <Link
                onClick={() => {
                  analytics.trackCustomEvent("user_action", {
                    action: "back_to_message_groups",
                    element: "breadcrumb_link",
                    from_group: selectedGroup.current?.name,
                    organization_id: organizationId,
                  });
                  setIsAIBotChat(false);
                  setMode("list");
                }}
              >
                Message Groups
              </Link>
              <Typography>{`${selectedGroup.current?.name || 'Chat'}`}</Typography>
            </Breadcrumbs>
          </Box>

          <Box
            sx={{
              flex: 1, // Take remaining space
              overflow: "hidden", // Prevent overflow at this level
              display: "flex",
              flexDirection: "column",
            }}
            data-testid="chat-container"
          >
            {isAIBotChat ? (
              <AISupportChat />
            ) : (() => {
              // Derive the client (non-coach) user from the selected group so the
              // avatar can be shown even for messages that predate storing avatarUrl
              // in the fromUser snapshot.
              const clientUid = selectedGroup.current?.users?.find(u => u !== uid);
              const clientUser = (users || []).find(u => u.uid === clientUid);
              return (
                <ChatParticipantProvider value={{ clientAvatarUrl: clientUser?.avatarUrl, clientUid }}>
                  <ModernChatWrapper
                    groupId={selectedGroup.current?.id}
                    uid={uid}
                    width="100%"
                    height="100%"
                  />
                </ChatParticipantProvider>
              );
            })()}
          </Box>
        </Box>
      </PageContent>
    );
  }
};

export default MessagesPage;
